import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

const MIN_REASON_LENGTH = 10;

/**
 * Block / unblock confirmation.
 *
 * Blocking requires a reason: it is stored on the account and included in the
 * suspension email, so the user is told why they lost access.
 */
const BlockUserModal = ({
  show,
  mode = "block",
  user,
  onConfirm,
  onCloseClick,
  isPending = false,
}) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset between openings, otherwise the previous user's reason is prefilled.
  useEffect(() => {
    if (show) {
      setReason("");
      setTouched(false);
    }
  }, [show]);

  const isBlocking = mode === "block";
  const trimmedReason = reason.trim();
  const isReasonTooShort = trimmedReason.length < MIN_REASON_LENGTH;
  const showError = touched && isReasonTooShort;

  const displayName =
    [user?.profile?.first_name, user?.profile?.last_name]
      .filter(Boolean)
      .join(" ") ||
    user?.username ||
    user?.email ||
    "";

  const handleConfirm = () => {
    if (isBlocking) {
      setTouched(true);
      if (isReasonTooShort) return;
      onConfirm(trimmedReason);
      return;
    }
    onConfirm();
  };

  return (
    <Modal isOpen={show} toggle={onCloseClick} centered>
      <ModalHeader toggle={onCloseClick}>
        {isBlocking ? "Bloquer l'accès" : "Réactiver l'accès"}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted">
          {isBlocking ? "Vous êtes sur le point de bloquer " : "Vous êtes sur le point de réactiver "}
          <strong className="text-body">{displayName}</strong>
          {user?.email ? ` (${user.email})` : ""}.
        </p>

        {isBlocking ? (
          <>
            <div className="alert alert-warning" role="alert">
              <i className="ri-alert-line align-bottom me-1"></i>
              L&apos;utilisateur ne pourra plus se connecter, ses sessions en
              cours seront fermées, et il recevra un e-mail lui indiquant le
              motif.
            </div>

            <div className="mb-2">
              <Label className="form-label" htmlFor="block-reason">
                Motif du blocage <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                id="block-reason"
                type="textarea"
                rows="3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                invalid={showError}
                placeholder="Ex : non-respect des conditions d'utilisation..."
                disabled={isPending}
              />
              {showError ? (
                <FormFeedback type="invalid" className="d-block">
                  Le motif doit contenir au moins {MIN_REASON_LENGTH}{" "}
                  caractères.
                </FormFeedback>
              ) : (
                <small className="text-muted">
                  Ce motif sera communiqué à l&apos;utilisateur par e-mail.
                </small>
              )}
            </div>
          </>
        ) : (
          <div className="alert alert-info" role="alert">
            <i className="ri-information-line align-bottom me-1"></i>
            L&apos;utilisateur pourra de nouveau se connecter et recevra un
            e-mail de confirmation.
            {user?.blocked_reason ? (
              <div className="mt-2">
                <strong>Motif du blocage initial :</strong>{" "}
                {user.blocked_reason}
              </div>
            ) : null}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCloseClick} disabled={isPending}>
          Annuler
        </Button>
        <Button
          color={isBlocking ? "danger" : "success"}
          onClick={handleConfirm}
          disabled={isPending}
        >
          {isPending ? <Spinner size="sm" className="me-2" /> : null}
          {isBlocking ? "Bloquer le compte" : "Réactiver le compte"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

BlockUserModal.propTypes = {
  show: PropTypes.bool,
  mode: PropTypes.oneOf(["block", "unblock"]),
  user: PropTypes.object,
  onConfirm: PropTypes.func,
  onCloseClick: PropTypes.func,
  isPending: PropTypes.bool,
};

export default BlockUserModal;
